import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AgGridReact } from '@ag-grid-community/react'
import { ColDef, GridApi, ModuleRegistry } from '@ag-grid-community/core'
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model'
import { useSelector } from 'react-redux'
import { Button, Form, DatePicker, Space, Alert } from 'antd'

import { downloadJSONasCSV } from 'helpers/download-file'
import { selectors as userSessionSelectors } from 'store/modules/user-session'
import { selectors as companySelectors } from 'store/modules/companies'
import ClientSelect from 'components/ClientSelect'
import PageWrapper from 'enrollment-platform/PageWrapper'
import Page from 'layouts/Page'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import RequirePermission from 'enrollment-platform/rbac/RequirePermission'
import { NoAdminAccess } from 'enrollment-platform/rbac/NoAdminAccess'
import { RbacActions, RbacSubjects } from 'enrollment-platform/rbac/constants'
import { FINANCE_PATH } from '../../constants'
import { useGetFundingBer } from '../../../FundingInvoice/api/funding-invoice-api-client'
import { FundingBerRowDto } from '../../../FundingInvoice/api/dto/funding-ber.dto'
import startCase from 'lodash/startCase'
import dayjs, { Dayjs } from 'dayjs'

ModuleRegistry.registerModules([ClientSideRowModelModule])

const COL_DEFS: ColDef<FundingBerRowDto>[] = [
  {
    field: 'employeeId',
    headerName: 'Employee ID',
    headerTooltip: 'Employee ID',
    width: 130,
  },
  {
    field: 'companyCode',
    headerName: 'Company Code',
    headerTooltip: 'Company Code',
    width: 130,
  },
  {
    field: 'divisionCode',
    headerName: 'Division Code',
    headerTooltip: 'Division Code',
    width: 90,
  },
  {
    field: 'department',
    headerName: 'Department',
    headerTooltip: 'Department',
    width: 130,
  },
  {
    field: 'departmentCode',
    headerName: 'Department Code',
    headerTooltip: 'Department Code',
    width: 140,
  },
  {
    field: 'firstName',
    headerName: 'First Name',
    headerTooltip: 'First Name',
    width: 120,
  },
  {
    field: 'lastName',
    headerName: 'Last Name',
    headerTooltip: 'Last Name',
    width: 120,
  },
  {
    field: 'relationship',
    headerName: 'Relationship',
    headerTooltip: 'Relationship',
    width: 130,
  },
  {
    field: 'carrierName',
    headerName: 'Carrier',
    headerTooltip: 'Carrier',
    width: 130,
  },
  {
    field: 'planCost',
    headerName: 'Plan Cost',
    headerTooltip: 'Plan Cost',
    width: 100,
  },
  {
    field: 'employerMonthlyContribution',
    headerName: 'ER Monthly Contribution',
    headerTooltip: 'Employer Monthly Contribution',
    width: 130,
  },
  {
    field: 'employeeMonthlyCost',
    headerName: 'EE Monthly Cost',
    headerTooltip: 'Employee Monthly Cost',
    width: 120,
  },
  {
    field: 'employeePayrollDeductionPreTax',
    headerName: 'EE Deduction Pre-Tax',
    headerTooltip: 'Employee Payroll Deduction Pre-Tax',
    width: 130,
  },
  {
    field: 'employeePayrollDeductionPostTax',
    headerName: 'EE Deduction Post-Tax',
    headerTooltip: 'Employee Payroll Deduction Post-Tax',
    width: 130,
  },
  {
    field: 'payrollCycleCode',
    headerName: 'Payroll Cycle',
    headerTooltip: 'Payroll Cycle Code',
    width: 110,
  },
  { field: 'notes', headerName: 'Notes', headerTooltip: 'Notes', width: 150 },
  {
    field: 'benefitId',
    headerName: 'Benefit ID',
    headerTooltip: 'Benefit ID',
    width: 130,
  },
  {
    field: 'benefitEffectiveDate',
    headerName: 'Benefit Effective Date',
    headerTooltip: 'Benefit Effective Date',
    width: 170,
  },
  {
    field: 'benefitEndDate',
    headerName: 'Benefit End Date',
    headerTooltip: 'Benefit End Date',
    width: 150,
  },
  {
    field: 'employmentStatus',
    headerName: 'Employment Status',
    headerTooltip: 'Employment Status',
    width: 150,
  },
]

const getUrlParams = (): {
  clientPublicKey: string
  startDate: string
  endDate: string
} => {
  const params = new URLSearchParams(window.location.search)
  return {
    clientPublicKey: params.get('clientPublicKey') || '',
    startDate: params.get('startDate') || '',
    endDate: params.get('endDate') || '',
  }
}

// The report lists employees, so it needs Read on US data. The gate wraps the
// whole page so nothing is fetched without it; EPS enforces the same rule.
export default function BenefitEffectiveReport() {
  return (
    <RequirePermission
      action={RbacActions.Read}
      subject={RbacSubjects.UsRestrictedData}
      fallback={
        <NoAdminAccess message="You don’t have access to US data, which this report requires. Please contact your administrator to request access, then refresh." />
      }
    >
      <BenefitEffectiveReportPage />
    </RequirePermission>
  )
}

function BenefitEffectiveReportPage() {
  const currentCompany = useSelector((state) =>
    userSessionSelectors.getCurrentCompany(state)
  ) as { Id: string; CompanyName: string }

  const companies = useSelector((state) => [
    { id: '', companyName: '(All)' },
    ...companySelectors.companyCollectionSelector(state),
  ])

  const urlParams = getUrlParams()
  const { Id: defaultCompanyId } = currentCompany

  const [selectedCompanyId, setSelectedCompanyId] = useState<string>(
    urlParams.clientPublicKey || defaultCompanyId
  )
  const [startDate, setStartDate] = useState<Dayjs>(
    urlParams.startDate ? dayjs(urlParams.startDate) : dayjs().startOf('year')
  )
  const [endDate, setEndDate] = useState<Dayjs>(
    urlParams.endDate ? dayjs(urlParams.endDate) : dayjs()
  )

  const queryParams = useMemo(
    () => ({
      clientPublicKey: selectedCompanyId,
      benefitEffectiveStartDate: startDate.format('YYYY-MM-DD'),
      benefitEffectiveEndDate: endDate.format('YYYY-MM-DD'),
    }),
    [selectedCompanyId, startDate, endDate]
  )

  const { data, isLoading, isError, error } = useGetFundingBer(queryParams, {
    enabled: !!selectedCompanyId,
  })

  const reportData = useMemo(() => data?.data ?? [], [data])

  const gridApi = useRef<GridApi | null>(null)

  useEffect(() => {
    const params = new URLSearchParams()
    if (selectedCompanyId) params.set('clientPublicKey', selectedCompanyId)
    params.set('startDate', startDate.format('YYYY-MM-DD'))
    params.set('endDate', endDate.format('YYYY-MM-DD'))
    const newUrl = `${window.location.pathname}?${params.toString()}`
    window.history.replaceState({}, '', newUrl)
  }, [selectedCompanyId, startDate, endDate])

  const handleCompanyChange = useCallback((value: string) => {
    setSelectedCompanyId(value)
  }, [])

  const handleStartDate = useCallback(
    (value: Dayjs | null) => {
      if (!value) return
      setStartDate(value)
      if (endDate <= value) {
        setEndDate(value.add(1, 'month'))
      }
    },
    [endDate]
  )

  const handleEndDate = useCallback((value: Dayjs | null) => {
    if (!value) return
    setEndDate(value)
  }, [])

  const onDownloadReportClick = useCallback(() => {
    const companyName =
      companies.find((c) => c.id === selectedCompanyId)?.companyName ?? ''
    downloadJSONasCSV(
      reportData.map((row) =>
        Object.fromEntries(
          Object.entries(row).map(([key, value]) => [startCase(key), value])
        )
      ),
      `${companyName} Benefit Effective Report ${endDate.format('YYYY-MM-DD')}.csv`
    )
  }, [companies, selectedCompanyId, reportData, endDate])

  useEffect(() => {
    if (!gridApi.current) return
    if (isLoading) {
      gridApi.current.showLoadingOverlay()
    } else {
      gridApi.current.hideOverlay()
    }
  }, [isLoading])

  return (
    <PageWrapper>
      <Page
        title="Benefit Effective Report"
        maxWidth="1600px"
        breadcrumb={
          <BreadcrumbNav
            items={[
              { title: 'Finance', path: FINANCE_PATH },
              { title: 'Benefit Effective Report' },
            ]}
          />
        }
      >
        <div
          className="ag-theme-quartz"
          style={{
            height: 'calc(100vh - 320px)',
            minHeight: 200,
            width: '100%',
          }}
        >
          <div style={{ float: 'right' }}>
            <Button
              type="primary"
              disabled={reportData.length === 0}
              onClick={onDownloadReportClick}
              style={{ marginTop: 30 }}
            >
              Download report
            </Button>
          </div>
          <Space>
            <Form.Item label="Company" layout="vertical">
              <ClientSelect
                companies={companies}
                defaultCompanyId={selectedCompanyId}
                onChange={handleCompanyChange}
              />
            </Form.Item>
            <Form.Item label="Start date" layout="vertical">
              <DatePicker value={startDate} onChange={handleStartDate} />
            </Form.Item>
            <Form.Item label="End date" layout="vertical">
              <DatePicker value={endDate} onChange={handleEndDate} />
            </Form.Item>
          </Space>
          {isError && (
            <Alert
              message="Error"
              description={
                error instanceof Error
                  ? error.message
                  : 'An error occurred while fetching the report.'
              }
              type="error"
              showIcon
              closable
              style={{ marginTop: 16, marginBottom: 16 }}
            />
          )}
          <AgGridReact
            rowData={reportData}
            columnDefs={COL_DEFS}
            defaultColDef={{
              sortable: true,
              resizable: true,
              filter: true,
            }}
            tooltipShowDelay={300}
            onGridReady={(params) => {
              gridApi.current = params.api
              if (isLoading) {
                params.api.showLoadingOverlay()
              } else {
                params.api.hideOverlay()
              }
            }}
          />
        </div>
      </Page>
    </PageWrapper>
  )
}
